import React, { useCallback, useMemo, useState } from 'react';
import { DateRange } from 'react-day-picker';
import { useCoverage } from '../Coverage/CoverageContext';
import { useDateTimeFormat } from '../../conversions/useDateFormat';
import { PeriodNavigationActions } from './PeriodNavigationActions';

interface CoverageActionsProps {
  setDisableDragNnDropTooltip: React.Dispatch<React.SetStateAction<boolean>>;
  isDragging: boolean;
  variant?: 'external' | 'inline';
}

export const CoverageActions = ({
  setDisableDragNnDropTooltip,
  isDragging,
  variant = 'external',
}: CoverageActionsProps) => {
  const {
    coverageDate,
    navigateToNextPeriod,
    navigateToPreviousPeriod,
    getNextPeriodDateRange,
    getPreviousPeriodDateRange,
    selectedSourceIdentifiers,
    loadingSourceIdentifiers,
    isLoadingPeriodNavigation,
    wouldNextPeriodBeInFuture,
  } = useCoverage();

  const [lastClicked, setLastClicked] = useState<string | null>(null);
  const { formatDateTimeWithTimeZone } = useDateTimeFormat();

  const onNext = useCallback(async () => {
    if (!coverageDate || wouldNextPeriodBeInFuture()) return;
    navigateToNextPeriod();
  }, [coverageDate, wouldNextPeriodBeInFuture, navigateToNextPeriod]);

  const onPrevious = useCallback(async () => {
    if (!coverageDate) return;
    navigateToPreviousPeriod();
  }, [coverageDate, navigateToPreviousPeriod]);

  // Memoize expensive calculations to prevent re-renders
  const { isLoading, isPreviousDisabled, isNextDisabled } = useMemo(() => {
    const loading =
      loadingSourceIdentifiers.size > 0 || isLoadingPeriodNavigation;
    const previousDisabled = !coverageDate;
    const nextDisabled = !coverageDate || wouldNextPeriodBeInFuture();

    return {
      isLoading: loading,
      isPreviousDisabled: previousDisabled,
      isNextDisabled: nextDisabled,
    };
  }, [
    loadingSourceIdentifiers.size,
    isLoadingPeriodNavigation,
    coverageDate,
    wouldNextPeriodBeInFuture,
  ]);

  const nextPeriodDateRange = useMemo(() => {
    return getNextPeriodDateRange();
  }, [getNextPeriodDateRange]);

  const previousPeriodDateRange = useMemo(() => {
    return getPreviousPeriodDateRange();
  }, [getPreviousPeriodDateRange]);

  // Memoize period range formatting to prevent re-computation
  const tooltips = useMemo(() => {
    const formatPeriodRange = (dateRange: DateRange | undefined): string => {
      if (!dateRange?.from || !dateRange?.to) return '';

      const fromStr = formatDateTimeWithTimeZone(dateRange.from);
      const toStr = formatDateTimeWithTimeZone(dateRange.to);

      return `${fromStr} to ${toStr}`;
    };

    return {
      previousTooltip: previousPeriodDateRange
        ? `Switch to coverage for period ${formatPeriodRange(
            previousPeriodDateRange
          )}`
        : 'Switch to previous coverage period',
      nextTooltip: nextPeriodDateRange
        ? `Switch to coverage for period ${formatPeriodRange(
            nextPeriodDateRange
          )}`
        : 'Switch to next coverage period',
    };
  }, [
    previousPeriodDateRange,
    nextPeriodDateRange,
    formatDateTimeWithTimeZone,
  ]);

  // Memoize the entire config object to prevent PeriodNavigationActions re-renders
  const config = useMemo(
    () => ({
      onNext,
      onPrevious,
      isPreviousDisabled,
      isNextDisabled,
      isLoading,
      lastClicked,
      setLastClicked,
      previousTooltip: tooltips.previousTooltip,
      nextTooltip: tooltips.nextTooltip,
      shouldShow: selectedSourceIdentifiers.length > 0,
    }),
    [
      onNext,
      onPrevious,
      isPreviousDisabled,
      isNextDisabled,
      isLoading,
      lastClicked,
      setLastClicked,
      tooltips.previousTooltip,
      tooltips.nextTooltip,
      selectedSourceIdentifiers.length,
    ]
  );

  return (
    <PeriodNavigationActions
      setDisableDragNnDropTooltip={setDisableDragNnDropTooltip}
      isDragging={isDragging}
      variant={variant}
      config={config}
    />
  );
};
